import {z} from 'zod';
import {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {
  addBrandAppDomain,
  addBrandEmailDomain,
  getBrand,
  removeBrandAppDomain,
  removeBrandEmailDomain,
  updateBrand,
  updateBrandLogo,
  updateBrandThumbnail,
  verifyBrandAppDomain,
  verifyBrandEmailDomain,
  VerdocsEndpoint,
  type IBrand,
} from '@verdocs/js-sdk';
import {ArrowPathIcon, ClipboardDocumentIcon, PencilSquareIcon, TrashIcon} from '@heroicons/react/24/outline';
import {CheckCircleIcon, XCircleIcon} from '@heroicons/react/24/solid';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  FlexFill,
  FormLabel,
  PhotoSelector,
  Spinner,
  TextInput,
} from '../../Components';
import {invalidateAllQueries, useOrganization} from '../../react-sdk/queries';
import {SigningLinkPreview, SigningPagePreview, type THostingState} from './SigningLinkPreview';
import {CertificationPreview, DEFAULT_PDF_LOCATION, DEFAULT_PDF_REASON} from './CertificationPreview';
import {InboxPreview, type TSenderState} from './InboxPreview';
import {useRole} from '../../hooks/useRole';
import {OkCancelDialog} from '../../Dialogs';
import {AppState} from '../../AppState';

export type TBrandSection = 'communications' | 'hosting' | 'signing' | 'certification';

const schema = z.object({
  email_sender_name: z.string().trim().max(120).optional().or(z.literal('')),
  full_logo_url: z.string().trim().optional(),
  thumbnail_url: z.string().trim().optional(),
  support_contact: z.string().trim().optional().or(z.literal('')),
  powered_by_label: z.string().trim().max(120).optional().or(z.literal('')),
  powered_by_url: z.string().trim().optional().or(z.literal('')),
  terms_use_url: z.string().trim().optional().or(z.literal('')),
  privacy_policy_url: z.string().trim().optional().or(z.literal('')),
  page_title: z.string().trim().max(100).optional().or(z.literal('')),
  favicon_url: z.string().trim().optional().or(z.literal('')),
  pdf_signature_reason: z.string().trim().optional().or(z.literal('')),
  pdf_signature_location: z.string().trim().optional().or(z.literal('')),
  disclaimer: z.string().optional().or(z.literal('')),
});

type SchemaType = z.infer<typeof schema>;

const emailDomainSchema = z.object({
  subdomain: z
    .string()
    .trim()
    .min(3)
    .max(253)
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
      'Must be a valid domain (e.g. acme.com or notify.acme.com)',
    ),
  local_part: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._-]*$/i, 'Letters, numbers, dots, hyphens, underscores'),
  reply_to: z.string().trim().email('Must be a valid email').optional().or(z.literal('')),
});

const DEFAULT_APP_HOST = 'app.verdocs.com';

// Three or more labels, because Cloudflare for SaaS cannot serve an apex domain on our plan. The api has the final
// word (it also knows about taken subdomains and the blocklist).
const appDomainSchema = z
  .string()
  .trim()
  .max(253)
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?){2,}$/, 'Use a subdomain like sign.acme.com.')
  .refine((host) => !/(^|\.)verdocs\.(com|io)$/.test(host), 'Verdocs domains cannot be used.');

const APP_DOMAIN_NOTES: Record<string, string> = {
  pending:
    'Add the record below in your DNS provider, then click Verify DNS record. The certificate usually issues within minutes of the record resolving, though DNS can take up to 48 hours.',
  active: 'Signing links for this brand open on this domain.',
  failed:
    'We could not verify this domain. Check the record below, then verify again. Domains still pending after 14 days are marked failed.',
  suspended:
    'The record below stopped pointing at us, so signing links went back to app.verdocs.com. Restore it and verify again within 7 days, or the domain is removed.',
};

const appDomainErrorMessage = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong.';

const getEditableFields = (brand?: IBrand) => ({
  email_sender_name: brand?.email_sender_name || '',
  full_logo_url: brand?.full_logo_url || '',
  thumbnail_url: brand?.thumbnail_url || '',
  support_contact: brand?.support_contact || '',
  powered_by_label: brand?.powered_by_label || '',
  powered_by_url: brand?.powered_by_url || '',
  terms_use_url: brand?.terms_use_url || '',
  privacy_policy_url: brand?.privacy_policy_url || '',
  page_title: brand?.page_title || '',
  favicon_url: brand?.favicon_url || '',
  pdf_signature_reason: brand?.pdf_signature_reason || '',
  pdf_signature_location: brand?.pdf_signature_location || '',
  disclaimer: brand?.disclaimer || '',
});

const StatusBadge: FC<{status: string | null | undefined}> = ({status}) => {
  if (!status) return <span className="text-gray-400 italic text-xs">Not configured</span>;
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    verified: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    suspended: 'bg-gray-100 text-gray-800',
  };
  return <span className={`${styles[status] || 'bg-gray-100 text-gray-800'} text-xs font-medium px-2.5 py-0.5 rounded`}>{status}</span>;
};

const VerificationIcon: FC<{verified: boolean}> = ({verified}) =>
  verified ? <CheckCircleIcon className="size-5 text-green-600" /> : <XCircleIcon className="size-5 text-gray-300" />;

const DnsRecord: FC<{type: string; host: string; value: string; nameHint?: boolean}> = ({type, host, value, nameHint}) => {
  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
      <div className="mb-2 text-xs font-semibold uppercase text-slate-500">{type} Record</div>
      {[
        ['Name', host],
        ['Value', value],
      ].map(([label, text]) => (
        <div key={label} className="mb-2 flex items-center gap-2 last:mb-0">
          <span className="w-12 shrink-0 text-xs font-medium text-slate-500">{label}</span>
          <div className="min-w-0 flex-1 select-all break-all rounded border bg-white p-2 font-mono text-sm dark:bg-slate-900">{text}</div>
          <button
            type="button"
            onClick={() => copy(text, label)}
            className="flex shrink-0 items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-600 hover:text-[#654dcb] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300">
            <ClipboardDocumentIcon className="size-4" />
            Copy
          </button>
        </div>
      ))}
      {nameHint && (
        <p className="mt-2 text-xs text-slate-400">Some DNS providers add your domain to the name automatically. If yours does, enter only the part before your domain.</p>
      )}
    </div>
  );
};

/** A tab's lead card: what the surface is, and the button that opens its builder. */
const LeadCard: FC<{title: string; blurb: string; action: string; onAction: () => void; children?: ReactNode}> = ({
  title,
  blurb,
  action,
  onAction,
  children,
}) => (
  <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 tablet:flex-row tablet:items-center">
    <div className="flex-1">
      <b className="block text-sm font-semibold text-gray-800">{title}</b>
      <span className="block text-xs leading-relaxed text-gray-500">{blurb}</span>
      {children}
    </div>
    <VerdocsButton label={action} size="small" onClick={onAction} />
  </div>
);

const SectionTitle: FC<{children: ReactNode; help?: ReactNode}> = ({children, help}) => (
  <div className="mb-4 flex flex-row items-center">
    <h3 className="text-lg font-semibold text-[#33364b] dark:text-white">{children}</h3>
    <FlexFill />
    {help}
  </div>
);

interface BrandSectionsProps {
  brandId: string;
  section: TBrandSection;
  onOpenBuilder: () => void;
  onOpenEmailBuilder: () => void;
  onOpenDisclosures: () => void;
}

/**
 * The editable content behind each Brand Hub tab. One form covers every field so a section's
 * Save writes only what its inputs changed, and the sender-domain flow keeps its own buttons
 * because those calls are not part of the brand update.
 */
export const BrandSections: FC<BrandSectionsProps> = ({brandId, section, onOpenBuilder, onOpenEmailBuilder, onOpenDisclosures}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const orgQuery = useOrganization(organizationId);
  const {isPersonal} = useRole();
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [newThumbnail, setNewThumbnail] = useState<File | null>(null);
  const [working, setWorking] = useState(false);
  const [brand, setBrand] = useState<IBrand | null>(null);

  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [emailSubdomain, setEmailSubdomain] = useState('');
  const [emailLocalPart, setEmailLocalPart] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');
  const [confirmingRemoveEmail, setConfirmingRemoveEmail] = useState(false);
  const [appSubdomain, setAppSubdomain] = useState('');
  const [confirmingRemoveApp, setConfirmingRemoveApp] = useState(false);

  const logoPreviewUrl = useMemo(() => (newLogo ? URL.createObjectURL(newLogo) : null), [newLogo]);
  const thumbPreviewUrl = useMemo(() => (newThumbnail ? URL.createObjectURL(newThumbnail) : null), [newThumbnail]);
  useEffect(
    () => () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      if (thumbPreviewUrl) URL.revokeObjectURL(thumbPreviewUrl);
    },
    [logoPreviewUrl, thumbPreviewUrl],
  );

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () =>
      getBrand(VerdocsEndpoint.getDefault(), organizationId, brandId).then((b) => {
        setBrand(b);
        return getEditableFields(b);
      }),
  });

  const onSubmit = async (data: SchemaType) => {
    setWorking(true);
    try {
      const updated = await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brandId, {
        // Colors and style_overrides belong to the Theme Editor and Email Builder, and logos go through their
        // own upload calls, so a tab Save leaves them alone.
        email_sender_name: data.email_sender_name?.trim() || null,
        support_contact: data.support_contact?.trim() || null,
        powered_by_label: data.powered_by_label?.trim() || null,
        powered_by_url: data.powered_by_url?.trim() || null,
        terms_use_url: data.terms_use_url?.trim() || null,
        privacy_policy_url: data.privacy_policy_url?.trim() || null,
        page_title: data.page_title?.trim() || null,
        favicon_url: data.favicon_url?.trim() || null,
        pdf_signature_reason: data.pdf_signature_reason?.trim() || null,
        pdf_signature_location: data.pdf_signature_location?.trim() || null,
      });
      setBrand(updated);
      form.reset(getEditableFields(updated));
      invalidateAllQueries();
      toast.success('Brand updated');
    } catch (e: any) {
      toast.error(`Error updating brand: ${e.response?.data?.error || e.message}`);
    }

    if (newLogo) {
      try {
        const updated = await updateBrandLogo(VerdocsEndpoint.getDefault(), organizationId, brandId, newLogo);
        setBrand(updated);
        setNewLogo(null);
        toast.success('Logo uploaded');
      } catch (e: any) {
        toast.error(`Error uploading logo: ${e.response?.data?.error || e.message}`);
      }
    }

    if (newThumbnail) {
      try {
        const updated = await updateBrandThumbnail(VerdocsEndpoint.getDefault(), organizationId, brandId, newThumbnail);
        setBrand(updated);
        setNewThumbnail(null);
        toast.success('Thumbnail uploaded');
      } catch (e: any) {
        toast.error(`Error uploading thumbnail: ${e.response?.data?.error || e.message}`);
      }
    }

    setWorking(false);
  };

  const handleAddEmailDomain = async () => {
    const result = emailDomainSchema.safeParse({
      subdomain: emailSubdomain,
      local_part: emailLocalPart,
      reply_to: emailReplyTo,
    });
    if (!result.success) {
      toast.error(result.error.issues[0]?.message || 'Invalid input');
      return;
    }
    setWorking(true);
    try {
      const updated = await addBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId, {
        subdomain: result.data.subdomain,
        local_part: result.data.local_part,
        reply_to: result.data.reply_to || undefined,
      });
      setBrand(updated);
      setShowEmailSetup(false);
      invalidateAllQueries();
      toast.success('Email domain configured');
    } catch (e: any) {
      toast.error(`Error configuring email domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const handleRemoveEmailDomain = async () => {
    setWorking(true);
    try {
      const updated = await removeBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      setConfirmingRemoveEmail(false);
      invalidateAllQueries();
      toast.success('Email domain removed');
    } catch (e: any) {
      toast.error(`Error removing email domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const handleVerifyEmailDomain = async () => {
    setWorking(true);
    try {
      const updated = await verifyBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      invalidateAllQueries();
      const allVerified = updated.email_dkim_verified && updated.email_dmarc_verified;
      if (allVerified) {
        toast.success('All DNS records verified. Your custom email domain is now active.');
      } else {
        const pending = [];
        if (!updated.email_dkim_verified) pending.push('DKIM');
        if (!updated.email_dmarc_verified) pending.push('DMARC');
        toast.info(`${pending.join(', ')} not yet verified. DNS changes can take up to 48 hours to propagate.`);
      }
    } catch (e: any) {
      toast.error(`Error verifying domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const handleAddAppDomain = async () => {
    const result = appDomainSchema.safeParse(appSubdomain);
    if (!result.success) {
      toast.error(result.error.issues[0]?.message || 'Invalid subdomain');
      return;
    }
    setWorking(true);
    try {
      const updated = await addBrandAppDomain(VerdocsEndpoint.getDefault(), organizationId, brandId, {subdomain: result.data});
      setBrand(updated);
      setAppSubdomain('');
      invalidateAllQueries();
      toast.success('Domain added. Add the two DNS records next.');
    } catch (e: any) {
      toast.error(appDomainErrorMessage(e));
    }
    setWorking(false);
  };

  const handleVerifyAppDomain = async () => {
    setWorking(true);
    try {
      const updated = await verifyBrandAppDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      invalidateAllQueries();
      if (updated.app_domain_status === 'active') {
        toast.success('Domain verified. Signing links for this brand now open on it.');
      } else {
        toast.info('Not verified yet. DNS changes can take up to 48 hours to propagate.');
      }
    } catch (e: any) {
      toast.error(appDomainErrorMessage(e));
    }
    setWorking(false);
  };

  const handleRemoveAppDomain = async () => {
    setWorking(true);
    try {
      const updated = await removeBrandAppDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      invalidateAllQueries();
      toast.success('Domain removed');
    } catch (e: any) {
      toast.error(appDomainErrorMessage(e));
    }
    setConfirmingRemoveApp(false);
    setWorking(false);
  };

  if (form.formState.isLoading || !brand) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner mode="dark" />
      </div>
    );
  }

  const dirty = form.formState.isDirty || !!newLogo || !!newThumbnail;

  const disclaimer = form.watch('disclaimer') || '';
  const logoThumbs = [logoPreviewUrl || form.getValues().full_logo_url, thumbPreviewUrl || form.getValues().thumbnail_url].filter(
    (url, i, all): url is string => !!url && all.indexOf(url) === i,
  );
  const footerLinkCount = [
    form.watch('powered_by_label'),
    form.watch('powered_by_url'),
    form.watch('terms_use_url'),
    form.watch('privacy_policy_url'),
  ].filter((v) => v?.trim()).length;
  const footerLinks = {
    poweredByLabel: form.watch('powered_by_label') || '',
    poweredByUrl: form.watch('powered_by_url') || '',
    termsUrl: form.watch('terms_use_url') || '',
    privacyUrl: form.watch('privacy_policy_url') || '',
  };
  // The From line as the api assembles it (generateEmailFrom): the brand's From name, then the
  // legacy display name from the custom sender form, then the sender's own name for personal
  // accounts or the organization name for everyone else.
  const profile = AppState.currentProfile;
  const fallbackFromName =
    brand.email_display_name ||
    (isPersonal ? `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() : orgQuery.data?.name || brand.organization?.name) ||
    'Your organization';
  const fromName = form.watch('email_sender_name')?.trim() || fallbackFromName;
  const senderState: TSenderState = !brand.email_domain
    ? 'default'
    : brand.email_domain_status === 'verified' && brand.email_local_part
      ? 'active'
      : 'pending';
  const fromAddress =
    senderState === 'active' ? `${brand.email_local_part}@${brand.email_domain}`.toLowerCase() : 'notifications@verdocs.com';
  const hostingState: THostingState = !brand.app_domain ? 'default' : brand.app_domain_status === 'active' ? 'active' : 'pending';
  // TODO: Read this straight off IBrand once js-sdk adds it
  const appDomainReason = (brand as IBrand & {app_domain_status_reason?: string | null}).app_domain_status_reason;
  const allEmailRecordsVerified = !!(brand.email_dkim_verified && brand.email_dmarc_verified);

  return (
    <div className="relative">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {section === 'communications' && (
            <div className="grid grid-cols-1 gap-8 desktop:grid-cols-[minmax(0,1fr)_minmax(260px,30%)]">
              <div className="min-w-0">
                {/* The inbox preview carries the Email Builder button on wide screens. */}
                <div className="desktop:hidden">
                  <LeadCard
                    title="The invitation email"
                    blurb="Logo, colors, footer image, and the wording in the email recipients get."
                    action="Email Builder"
                    onAction={onOpenEmailBuilder}
                  />
                </div>

                <SectionTitle>Sender</SectionTitle>
                <div className="mb-8">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">From name</label>
                  <div className="flex max-w-md items-center gap-2">
                    <input
                      type="text"
                      {...form.register('email_sender_name')}
                      placeholder={fallbackFromName}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                    <VerdocsButton
                      label="Save"
                      size="small"
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={working || !form.formState.dirtyFields.email_sender_name}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    The name recipients see in their inbox, whether mail goes out from Verdocs or from your own domain. Empty uses{' '}
                    {isPersonal ? "the sender's name" : "your organization's name"}.
                  </p>
                  <p className="mt-2 text-sm text-gray-600 desktop:hidden">
                    Recipients see:{' '}
                    <span className="font-medium text-gray-800">
                      {fromName} &lt;{fromAddress}&gt;
                    </span>
                  </p>
                </div>

                {brand.email_domain ? (
                  <div className="mb-4">
                    <div className="mb-4 rounded-xl border border-gray-200 px-5 py-4">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-xs font-medium text-gray-500">Custom sender</span>
                        <span className="font-semibold">
                          {brand.email_local_part}@{brand.email_domain}
                        </span>
                        <StatusBadge status={brand.email_domain_status} />
                        <FlexFill />
                        {!allEmailRecordsVerified && (
                          <button
                            type="button"
                            onClick={handleVerifyEmailDomain}
                            disabled={working}
                            className="ml-3 flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                            <ArrowPathIcon className={`size-4 ${working ? 'animate-spin' : ''}`} />
                            Verify DNS records
                          </button>
                        )}
                      </div>
                      {brand.email_reply_to && (
                        <p className="mt-1 text-sm text-gray-500">
                          Reply-to: {brand.email_reply_to}
                          {brand.email_reply_to_verified ? (
                            <span className="ml-2 text-xs font-medium text-green-600">verified</span>
                          ) : (
                            <span className="ml-2 text-xs font-medium text-yellow-600">unverified</span>
                          )}
                        </p>
                      )}
                      <div className="mt-2 flex items-start gap-8">
                        <p className="flex-1 text-xs leading-relaxed text-gray-500">
                          {allEmailRecordsVerified
                            ? 'All DNS records verified. Mail for this brand goes out from this address.'
                            : 'Add the records below in your DNS provider, then click Verify DNS records. Changes usually show up within an hour, though some providers take up to 48.'}
                        </p>
                        <button
                          type="button"
                          onClick={() => setConfirmingRemoveEmail(true)}
                          disabled={working}
                          className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-red-600 disabled:opacity-50">
                          <TrashIcon className="size-4" />
                          Remove sender
                        </button>
                      </div>
                    </div>

                    <Accordion type="multiple">

                      <AccordionItem value="dkim" className="border-gray-100">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                          <span className="flex items-center gap-2">
                            <VerificationIcon verified={brand.email_dkim_verified} />
                            DKIM records
                            <span className="text-xs font-normal text-gray-400">{brand.email_dkim_verified ? 'Verified' : '3 CNAME records'}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="mb-2 text-xs text-gray-400">
                            Adds a cryptographic signature to outgoing emails, proving they haven't been tampered with. Add all three.
                          </p>
                          {brand.email_dkim_tokens?.map((token, i) => (
                            <DnsRecord
                              key={token}
                              type={`CNAME (${i + 1} of 3)`}
                              host={`${token}._domainkey.${brand.email_domain}`}
                              value={`${token}.dkim.amazonses.com`}
                              nameHint={i === 0}
                            />
                          ))}
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="dmarc" className="border-gray-100">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                          <span className="flex items-center gap-2">
                            <VerificationIcon verified={brand.email_dmarc_verified} />
                            DMARC record
                            <span className="text-xs font-normal text-gray-400">{brand.email_dmarc_verified ? 'Verified' : '1 TXT record'}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="mb-2 text-xs text-gray-400">
                            Tells receiving mail servers what to do with mail that claims to be from your domain but fails its checks. If your
                            domain already has a DMARC record, any policy works and you don't need to add this one.
                          </p>
                          <DnsRecord type="TXT" host={`_dmarc.${brand.email_domain}`} value="v=DMARC1; p=none;" nameHint />
                          <p className="-mt-1 mb-1 text-xs text-gray-400">
                            p=none only monitors. Once every service that sends mail as your domain is set up, p=quarantine or p=reject also
                            protects it from spoofing.
                          </p>
                          <p className="text-xs text-gray-400">
                            To receive aggregate DMARC reports, you can optionally append{' '}
                            <code className="rounded bg-gray-100 px-1 dark:bg-gray-800"> rua=mailto:dmarc-reports@yourdomain.com</code> to the
                            value.
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                ) : showEmailSetup ? (
                  <div className="mb-4">
                    <div className="flex flex-col gap-5">
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                          Sending Domain <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={emailSubdomain}
                          onChange={(e) => setEmailSubdomain(e.target.value.toLowerCase())}
                          placeholder="notify.acme.com"
                          className="max-w-sm w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          You can use your primary domain (e.g. <code>acme.com</code>) or a dedicated subdomain (e.g.{' '}
                          <code>notify.acme.com</code>
                          ). A dedicated subdomain is recommended because it isolates your notification sending reputation from your main
                          domain. If deliverability issues arise, they won't affect your regular business email.
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                          From Address <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-1 max-w-sm">
                          <input
                            type="text"
                            value={emailLocalPart}
                            onChange={(e) => setEmailLocalPart(e.target.value)}
                            placeholder="notifications"
                            className="flex-1 px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                          />
                          <span className="text-sm text-gray-400">@{emailSubdomain || '...'}</span>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Reply-to Address</label>
                        <input
                          type="email"
                          value={emailReplyTo}
                          onChange={(e) => setEmailReplyTo(e.target.value)}
                          placeholder="support@acme.com"
                          className="max-w-sm w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                        />
                        <p className="text-xs text-gray-400 mt-1">Where replies go when a recipient responds to a notification.</p>
                      </div>
                    </div>

                    {emailSubdomain && emailLocalPart && (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-w-sm">
                        <p className="text-xs text-gray-500 mb-1">Recipients will see:</p>
                        <p className="text-sm font-medium">
                          {fromName} &lt;{emailLocalPart}@{emailSubdomain}&gt;
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3 mt-6">
                      <VerdocsButton label="Set Up Sender" size="small" onClick={handleAddEmailDomain} disabled={working} />
                      <VerdocsButton
                        label="Cancel"
                        size="small"
                        variant="outline"
                        onClick={() => setShowEmailSetup(false)}
                        disabled={working}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Mail goes out from notifications@verdocs.com. To send from your own domain instead, set up a custom sender and add its
                      DNS records (DKIM and DMARC) to prove you own the domain.
                    </p>
                    <VerdocsButton label="Set Up Sender" size="small" onClick={() => setShowEmailSetup(true)} />
                  </div>
                )}
              </div>

              <aside className="hidden desktop:block">
                <div className="sticky top-4">
                  <InboxPreview
                    brand={brand}
                    fromName={fromName}
                    fromAddress={fromAddress}
                    senderState={senderState}
                    onOpenEmailBuilder={onOpenEmailBuilder}
                  />
                </div>
              </aside>
            </div>
          )}

          {section === 'hosting' && (
            <div className="grid grid-cols-1 gap-8 desktop:grid-cols-[minmax(0,1fr)_minmax(260px,30%)]">
              <div className="min-w-0">
                <SectionTitle>Signing domain</SectionTitle>

                {brand.app_domain ? (
                  <div className="mb-4">
                    <div className="mb-4 rounded-xl border border-gray-200 px-5 py-4">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-xs font-medium text-gray-500">Custom domain</span>
                        <span className="font-semibold">{brand.app_domain}</span>
                        <StatusBadge status={brand.app_domain_status} />
                        <FlexFill />
                        {brand.app_domain_status !== 'active' && (
                          <button
                            type="button"
                            onClick={handleVerifyAppDomain}
                            disabled={working}
                            className="ml-3 flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                            <ArrowPathIcon className={`size-4 ${working ? 'animate-spin' : ''}`} />
                            Verify DNS record
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-start gap-8">
                        <div className="flex-1">
                          <p className="text-xs leading-relaxed text-gray-500">{APP_DOMAIN_NOTES[brand.app_domain_status || 'pending']}</p>
                          {brand.app_domain_status !== 'active' && appDomainReason && <p className="mt-1 text-xs text-gray-400">{appDomainReason}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfirmingRemoveApp(true)}
                          disabled={working}
                          className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-red-600 disabled:opacity-50">
                          <TrashIcon className="size-4" />
                          Remove domain
                        </button>
                      </div>
                    </div>

                    <Accordion type="multiple">
                      <AccordionItem value="routing" className="border-gray-100">
                        <AccordionTrigger className="py-3 text-sm hover:no-underline">
                          <span className="flex items-center gap-2">
                            <VerificationIcon verified={brand.app_domain_status === 'active'} />
                            Routing record
                            <span className="text-xs font-normal text-gray-400">1 CNAME record</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="mb-2 text-xs text-gray-400">Points your subdomain at Verdocs so signing links resolve to us.</p>
                          <DnsRecord type="CNAME" host={brand.app_domain} value="custom.verdocs.com" nameHint />
                        </AccordionContent>
                      </AccordionItem>

                    </Accordion>
                  </div>
                ) : (
                  <div className="mb-4">
                    <p className="mb-4 text-sm text-gray-500">
                      Signing links open on {DEFAULT_APP_HOST}. To open them on your own subdomain instead, add it here and point a
                      CNAME record at us.
                    </p>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Subdomain</label>
                    <div className="flex max-w-md items-center gap-2">
                      <input
                        type="text"
                        value={appSubdomain}
                        onChange={(e) => setAppSubdomain(e.target.value.trim().toLowerCase())}
                        placeholder="sign.acme.com"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      />
                      <VerdocsButton label="Add domain" size="small" onClick={handleAddAppDomain} disabled={working || !appSubdomain} />
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Use a subdomain like sign.acme.com. Apex domains like acme.com are not supported.</p>
                  </div>
                )}

                <p className="mt-6 text-xs text-gray-400">
                  Want recipients to stay inside your own app?{' '}
                  <a
                    href="https://developers.verdocs.com/docs/guides/tutorials/embedded-signing-in-react"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-indigo-600 hover:underline">
                    Embed the signer instead
                  </a>
                  .
                </p>
              </div>

              <aside className="hidden desktop:block">
                <div className="sticky top-4">
                  <SigningLinkPreview brand={brand} hostingState={hostingState} />
                </div>
              </aside>
            </div>
          )}

          {section === 'signing' && (
            <div className="grid grid-cols-1 gap-8 desktop:grid-cols-[minmax(0,1fr)_minmax(260px,30%)]">
              <div className="min-w-0">
                {/* The page preview carries the Theme Editor button on wide screens. */}
                <div className="desktop:hidden">
                  <LeadCard
                    title="The signing page"
                    blurb="Colors, fonts, buttons, fields, and rounding, edited with a live preview and published when you are ready."
                    action="Theme Editor"
                    onAction={onOpenBuilder}
                  />
                </div>

                <SectionTitle>Signing page</SectionTitle>
                <Accordion type="multiple">
                  <AccordionItem value="logos" className="border-gray-100">
                    <AccordionTrigger className="py-3 text-sm hover:no-underline">
                      <span className="flex items-center gap-2">
                        Logos
                        {logoThumbs.length ? (
                          <span className="flex items-center gap-1.5">
                            {logoThumbs.map((url) => (
                              <img key={url} src={url} alt="" className="h-5 max-w-[60px] rounded object-contain" />
                            ))}
                          </span>
                        ) : (
                          <span className="text-xs font-normal text-gray-400">Verdocs logos until you add yours</span>
                        )}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="flex flex-wrap gap-10">
                        <div>
                          <FormLabel>Logo</FormLabel>
                          <PhotoSelector newFile={newLogo} value={form.getValues().full_logo_url} onSelectFile={setNewLogo} />
                          <p className="mt-1 max-w-[220px] text-xs text-gray-400">Rectangular spots: the top of emails and the page header.</p>
                        </div>
                        <div>
                          <FormLabel>Thumbnail</FormLabel>
                          <PhotoSelector newFile={newThumbnail} value={form.getValues().thumbnail_url} onSelectFile={setNewThumbnail} />
                          <p className="mt-1 max-w-[220px] text-xs text-gray-400">Small, square spots where the full logo will not fit.</p>
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-gray-400">Shared with the Email Builder.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="disclosures" className="border-gray-100">
                    <AccordionTrigger className="py-3 text-sm hover:no-underline">
                      <span className="flex items-center gap-2">
                        Disclosures
                        <span className="text-xs font-normal text-gray-400">{disclaimer.trim() ? 'Custom text' : 'Standard text'}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="mb-3 text-sm text-gray-500">
                        {disclaimer.trim()
                          ? 'Custom text. Signers see it before they begin.'
                          : "Standard text with your brand's name and legal links. Signers see it before they begin."}
                      </p>
                      <button
                        type="button"
                        onClick={onOpenDisclosures}
                        className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800">
                        <PencilSquareIcon className="size-4" />
                        {disclaimer.trim() ? 'Edit disclosure text' : 'Customize disclosure text'}
                      </button>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="footer" className="border-gray-100">
                    <AccordionTrigger className="py-3 text-sm hover:no-underline">
                      <span className="flex items-center gap-2">
                        Footer and legal links
                        <span className="text-xs font-normal text-gray-400">
                          {footerLinkCount ? `${footerLinkCount} set` : 'Organization defaults'}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
                        <div>
                          <FormLabel>Powered by label</FormLabel>
                          <input
                            type="text"
                            {...form.register('powered_by_label')}
                            placeholder="Powered by Acme"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">Shown at the bottom of every signing page. Empty uses the organization default.</p>
                        </div>
                        <div>
                          <FormLabel>Powered by link</FormLabel>
                          <input
                            type="text"
                            {...form.register('powered_by_url')}
                            placeholder="https://"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">Where the label goes when clicked.</p>
                        </div>
                        <div>
                          <FormLabel>Terms of use link</FormLabel>
                          <input
                            type="text"
                            {...form.register('terms_use_url')}
                            placeholder="https://"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">Your own terms page, linked from the footer.</p>
                        </div>
                        <div>
                          <FormLabel>Privacy policy link</FormLabel>
                          <input
                            type="text"
                            {...form.register('privacy_policy_url')}
                            placeholder="https://"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">Your own privacy page, linked from the footer.</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="browser" className="border-gray-100">
                    <AccordionTrigger className="py-3 text-sm hover:no-underline">
                      <span className="flex items-center gap-2">
                        Browser tab
                        <span className="truncate text-xs font-normal text-gray-400">{form.watch('page_title') || brand.name || brand.key}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
                        <div>
                          <FormLabel>Page title</FormLabel>
                          <input
                            type="text"
                            {...form.register('page_title')}
                            placeholder={brand.name || brand.key}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">The browser tab title on signing and other hosted pages. Empty uses the brand name.</p>
                        </div>
                        <div>
                          <FormLabel>Favicon URL</FormLabel>
                          <input
                            type="text"
                            {...form.register('favicon_url')}
                            placeholder="https://"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          />
                          <p className="mt-1 text-xs text-gray-400">The small icon in the browser tab. Link to a 32x32 PNG.</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="mt-6">
                  <VerdocsButton type="submit" label="Save" size="normal" disabled={form.formState.isSubmitting || !dirty} />
                </div>
              </div>

              <aside className="hidden desktop:block">
                <div className="sticky top-4">
                  <SigningPagePreview
                    primaryColor={brand.primary_color || '#654dcb'}
                    secondaryColor={brand.secondary_color || '#55bc81'}
                    footer={footerLinks}
                    pageTitle={form.watch('page_title')?.trim() || brand.name || brand.key}
                    faviconUrl={form.watch('favicon_url')?.trim() || null}
                    logoUrl={thumbPreviewUrl || logoPreviewUrl || form.getValues().thumbnail_url || form.getValues().full_logo_url}
                    onOpenBuilder={onOpenBuilder}
                  />
                </div>
              </aside>
            </div>
          )}

          {section === 'certification' && (
            <div className="grid grid-cols-1 gap-8 desktop:grid-cols-[minmax(0,1fr)_minmax(260px,30%)]">
              <div className="min-w-0">
                <SectionTitle>Signature panel</SectionTitle>
                <p className="mb-5 text-sm text-gray-500">
                  Customize the details shown in Adobe's Signature Panel when viewing the final, signed documents.
                </p>

                <div className="flex max-w-md flex-col">
                  <TextInput label="Certified By" name="pdf_signature_reason" placeholder={DEFAULT_PDF_REASON} />
                  <TextInput label="Location" name="pdf_signature_location" placeholder={DEFAULT_PDF_LOCATION} />
                  <TextInput label="Contact info" name="support_contact" placeholder="e.g. support@acme.com" />
                </div>

                <div className="mt-2">
                  <VerdocsButton type="submit" label="Save" size="normal" disabled={form.formState.isSubmitting || !dirty} />
                </div>

                <div className="mt-8 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm leading-relaxed text-indigo-900">
                  Although you may customize the appearance of the signature panel, "Certificate Details..." will show Verdocs' signing
                  certificate. Enterprise clients that plan to provide their own AATL-approved signing certificates should contact{' '}
                  <a
                    href="mailto:support@verdocs.com?subject=Custom%20Signing%20Certificate%20Request"
                    className="font-medium text-indigo-700 underline hover:text-indigo-900">
                    support@verdocs.com
                  </a>{' '}
                  for assistance.
                </div>
              </div>

              <aside className="hidden desktop:block">
                <div className="sticky top-4">
                  <CertificationPreview
                    reason={form.watch('pdf_signature_reason') || ''}
                    location={form.watch('pdf_signature_location') || ''}
                    contact={form.watch('support_contact') || ''}
                  />
                </div>
              </aside>
            </div>
          )}

        </form>
      </FormProvider>

      {confirmingRemoveApp && (
        <OkCancelDialog onOk={handleRemoveAppDomain} onCancel={() => setConfirmingRemoveApp(false)}>
          <p className="mb-6">
            Remove {brand.app_domain}? Signing links for this brand go back to {DEFAULT_APP_HOST}, including links in invitations already
            sent.
          </p>
        </OkCancelDialog>
      )}

      {confirmingRemoveEmail && (
        <OkCancelDialog onOk={handleRemoveEmailDomain} onCancel={() => setConfirmingRemoveEmail(false)}>
          <p className="mb-6">
            Are you sure you want to remove the custom email domain? Notifications will revert to sending from the default Verdocs address.
          </p>
        </OkCancelDialog>
      )}

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};
